import React, { useMemo } from 'react';
import { Box, Typography, Chip, CircularProgress, LinearProgress, useTheme } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

const getDiffStyles = (mode) => {
  const isDark = mode === 'dark';
  return {
    variables: {
      light: {
        diffViewerBackground: isDark ? '#232327' : '#FFFFFF',
        diffViewerColor: isDark ? '#fafafa' : '#171717',
        addedBackground: isDark ? 'rgba(34, 197, 94, 0.1)' : '#F0FDF4',
        addedColor: isDark ? '#86EFAC' : '#166534',
        removedBackground: isDark ? 'rgba(239, 68, 68, 0.1)' : '#FEF2F2',
        removedColor: isDark ? '#FCA5A5' : '#991B1B',
        wordAddedBackground: isDark ? 'rgba(34, 197, 94, 0.25)' : '#BBF7D0',
        wordRemovedBackground: isDark ? 'rgba(239, 68, 68, 0.25)' : '#FECACA',
        addedGutterBackground: isDark ? 'rgba(34, 197, 94, 0.15)' : '#DCFCE7',
        removedGutterBackground: isDark ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2',
        gutterBackground: isDark ? '#1a1a1e' : '#FAFAFA',
        gutterBackgroundDark: isDark ? '#27272a' : '#F5F5F5',
        highlightBackground: isDark ? 'rgba(250, 204, 21, 0.15)' : '#FEF9C3',
        highlightGutterBackground: isDark ? 'rgba(250, 204, 21, 0.2)' : '#FEF08A',
        codeFoldGutterBackground: isDark ? '#27272a' : '#F5F5F5',
        codeFoldBackground: isDark ? '#1a1a1e' : '#FAFAFA',
        emptyLineBackground: isDark ? '#1a1a1e' : '#FAFAFA',
        gutterColor: isDark ? '#71717a' : '#737373',
        addedGutterColor: isDark ? '#86EFAC' : '#166534',
        removedGutterColor: isDark ? '#FCA5A5' : '#991B1B',
        codeFoldContentColor: isDark ? '#a1a1aa' : '#525252',
        diffViewerTitleBackground: isDark ? '#27272a' : '#F5F5F5',
        diffViewerTitleColor: isDark ? '#fafafa' : '#171717',
        diffViewerTitleBorderColor: isDark ? '#3f3f46' : '#E5E5E5',
      },
    },
    line: {
      padding: '4px 8px',
      fontSize: '13px',
      fontFamily: '"JetBrains Mono", "Fira Code", "Monaco", monospace',
      lineHeight: '1.6',
    },
    gutter: {
      padding: '4px 12px',
      fontSize: '12px',
      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
      minWidth: '50px',
    },
    codeFold: {
      fontSize: '13px',
      fontWeight: 500,
      color: isDark ? '#71717a' : '#737373',
      backgroundColor: isDark ? '#27272a' : '#F5F5F5',
      padding: '8px 16px',
    },
    codeFoldGutter: {
      backgroundColor: isDark ? '#27272a' : '#F5F5F5',
    },
    diffContainer: {
      borderRadius: '0',
      border: 'none',
      display: 'block',
    },
    contentText: {
      fontSize: '13px',
      lineHeight: '1.6',
    },
  };
};

// Styled components
const Container = styled(Box)({
  minHeight: '400px',
});

const DiffHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.subtle,
}));

const HeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

const HeaderRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const StatChip = styled(Box)(({ type, theme }) => {
  const isDark = theme.palette.mode === 'dark';
  const colors = {
    added: {
      bg: isDark ? 'rgba(34, 197, 94, 0.15)' : '#F0FDF4',
      text: isDark ? '#86EFAC' : '#166534',
      icon: isDark ? '#86EFAC' : '#22C55E',
    },
    removed: {
      bg: isDark ? 'rgba(239, 68, 68, 0.15)' : '#FEF2F2',
      text: isDark ? '#FCA5A5' : '#991B1B',
      icon: isDark ? '#FCA5A5' : '#EF4444',
    },
    unchanged: {
      bg: theme.palette.action.hover,
      text: theme.palette.text.secondary,
      icon: theme.palette.text.secondary,
    },
  };
  const c = colors[type] || colors.unchanged;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '6px',
    backgroundColor: c.bg,
    color: c.text,
    fontSize: '13px',
    fontWeight: 500,
    '& svg': {
      fontSize: '16px',
      color: c.icon,
    },
  };
});

const DiffContent = styled(Box)({
  '& pre': {
    margin: 0,
    padding: 0,
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
  },
  '& td': {
    verticalAlign: 'top',
  },
});

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const LoadingOverlay = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  gap: '16px',
});

const LoadingIcon = styled(CompareArrowsIcon)(({ theme }) => ({
  fontSize: '40px',
  color: theme.palette.text.disabled,
  animation: `${pulse} 1.5s ease-in-out infinite`,
}));

const EmptyState = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '60px 20px',
  textAlign: 'center',
});

const EmptyIcon = styled(CheckCircleOutlineIcon)(({ theme }) => ({
  fontSize: '48px',
  color: theme.palette.success.main,
  marginBottom: '16px',
}));

const ColumnHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ColumnTitle = styled(Box)(({ side, theme }) => ({
  flex: 1,
  padding: '12px 16px',
  backgroundColor: theme.palette.background.subtle,
  borderRight: side === 'left' ? `1px solid ${theme.palette.divider}` : 'none',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

const VersionBadge = styled(Chip)(({ theme }) => ({
  height: '24px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  '& .MuiChip-label': {
    padding: '0 10px',
  },
}));

function Schema({ jsonOne, jsonTwo, view, compareLoad, deferredRender, versionInfo }) {
  const theme = useTheme();
  const showSplitView = view === 'split_view';
  const diffStyles = useMemo(() => getDiffStyles(theme.palette.mode), [theme.palette.mode]);

  const { oldValue, newValue } = useMemo(() => ({
    oldValue: JSON.stringify(jsonOne, null, 2) || '',
    newValue: JSON.stringify(jsonTwo, null, 2) || '',
  }), [jsonOne, jsonTwo]);

  // Calculate diff stats
  const diffStats = useMemo(() => {
    if (!oldValue || !newValue) return { added: 0, removed: 0, unchanged: true };

    const oldLines = oldValue.split('\n');
    const newLines = newValue.split('\n');

    // Simple line-based diff estimation
    const oldSet = new Set(oldLines);
    const newSet = new Set(newLines);

    let added = 0;
    let removed = 0;

    newLines.forEach(line => {
      if (!oldSet.has(line) && line.trim()) added++;
    });

    oldLines.forEach(line => {
      if (!newSet.has(line) && line.trim()) removed++;
    });

    return {
      added,
      removed,
      unchanged: added === 0 && removed === 0,
    };
  }, [oldValue, newValue]);

  // Loading state
  if (compareLoad) {
    return (
      <Container>
        <LinearProgress
          sx={{
            height: '2px',
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'text.primary',
            },
          }}
        />
        <LoadingOverlay>
          <LoadingIcon />
          <Typography sx={{ fontSize: '15px', fontWeight: 500, color: 'text.secondary' }}>
            Comparing versions...
          </Typography>
          <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
            Analyzing workflow changes
          </Typography>
        </LoadingOverlay>
      </Container>
    );
  }

  // Wait for deferred render
  if (!deferredRender) {
    return (
      <Container>
        <LinearProgress
          sx={{
            height: '2px',
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              bgcolor: 'text.primary',
            },
          }}
        />
        <LoadingOverlay>
          <CircularProgress size={32} sx={{ color: 'text.secondary' }} />
          <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
            Rendering diff...
          </Typography>
        </LoadingOverlay>
      </Container>
    );
  }

  // No changes state
  if (diffStats.unchanged) {
    return (
      <Container>
        <DiffHeader>
          <HeaderLeft>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
              Comparison Result
            </Typography>
          </HeaderLeft>
          <HeaderRight>
            <StatChip type="unchanged">
              <CheckCircleOutlineIcon />
              No changes
            </StatChip>
          </HeaderRight>
        </DiffHeader>
        <EmptyState>
          <EmptyIcon />
          <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'text.primary', mb: 1 }}>
            No differences found
          </Typography>
          <Typography sx={{ fontSize: '14px', color: 'text.secondary', maxWidth: '400px' }}>
            {versionInfo?.v1?.version === versionInfo?.v2?.version
              ? 'You are comparing the same version. Select different versions to see changes.'
              : 'These versions are identical. The workflow configuration has not changed between these versions.'}
          </Typography>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header with stats */}
      <DiffHeader>
        <HeaderLeft>
          <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
            Changes
          </Typography>
        </HeaderLeft>
        <HeaderRight>
          {diffStats.added > 0 && (
            <StatChip type="added">
              <AddIcon />
              {diffStats.added} added
            </StatChip>
          )}
          {diffStats.removed > 0 && (
            <StatChip type="removed">
              <RemoveIcon />
              {diffStats.removed} removed
            </StatChip>
          )}
        </HeaderRight>
      </DiffHeader>

      {/* Column headers for split view */}
      {showSplitView && (
        <ColumnHeader>
          <ColumnTitle side="left">
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Previous</Typography>
            {versionInfo?.v1 && (
              <VersionBadge label={`v${versionInfo.v1.version}`} size="small" />
            )}
          </ColumnTitle>
          <ColumnTitle side="right">
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Current</Typography>
            {versionInfo?.v2 && (
              <VersionBadge label={`v${versionInfo.v2.version}`} size="small" />
            )}
          </ColumnTitle>
        </ColumnHeader>
      )}

      {/* Diff viewer */}
      <DiffContent>
        <ReactDiffViewer
          styles={diffStyles}
          compareMethod={DiffMethod.WORDS}
          oldValue={oldValue}
          newValue={newValue}
          splitView={showSplitView}
          showDiffOnly={true}
          extraLinesSurroundingDiff={3}
          codeFoldMessageRenderer={total => (
            <span style={{ padding: '8px 0' }}>
              ··· {total} unchanged {total === 1 ? 'line' : 'lines'} ···
            </span>
          )}
          useDarkTheme={false}
          hideLineNumbers={false}
          leftTitle=""
          rightTitle=""
        />
      </DiffContent>
    </Container>
  );
}

export default Schema;
